<script>
import $dayjs from '@/services/date'
import { mapActions, mapGetters } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import SubBubbleAgreementViewModel from '@/models/subBubbleAgreement/subBubbleAgreementViewModel'
import {
  createSubBubbleAgreementDTOMapper,
  updateSubBubbleAgreementDTOMapper,
} from '@/mappers/sub-bubble-agreement-dto-mapper'
import SubBubbleAgreementsList from './sub-bubble-agreements-list.vue'
import SubBubbleAgreementsListActions from './sub-bubble-agreements-list-actions.vue'
import SubBubbleAgreementsEditorDialog from './sub-bubble-agreements-editor-dialog.vue'
import SubBubbleAgreementsFailureBlock from './sub-bubble-agreements-failure-block.vue'

export default {
  name: 'SubBubbleAgreementsContainer',
  components: {
    SubBubbleAgreementsList,
    SubBubbleAgreementsListActions,
    SubBubbleAgreementsEditorDialog,
    SubBubbleAgreementsFailureBlock,
  },
  mixins: [baseMixin],
  props: {
    clientId: {
      type: Number,
      default: null,
    },
    clientGroupId: {
      type: Number,
      default: null,
    },
  },
  data: () => {
    return {
      subBubbleAgreements: [],
      subBubbleAgreementEditing: null,
      subBubbleAgreementDeleting: null,
      editorTitle: '',
      showEditorDialog: false,
      showConfirmDeleteDialog: false,
      hasLoaded: false,
      failedToLoad: false,
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'subBubbleAgreement/isLoading',
      isLoadingCRUD: 'subBubbleAgreement/isLoadingCRUD',
    }),
    isClientGroup() {
      return !!this.clientGroupId
    },
    includesNonOwnedAgreements() {
      if (this.isClientGroup)
        return this.subBubbleAgreements.some((x) => !!x.clientId)
      else return this.subBubbleAgreements.some((x) => !!x.clientGroupId)
    },
    nonOwnedAgreementsAlertText() {
      if (!this.includesNonOwnedAgreements) return ''

      return this.isClientGroup
        ? this.$t('subBubbleAgreements.nonOwnedAgreementsForGroupAlertText')
        : this.$t('subBubbleAgreements.nonOwnedAgreementsForClientAlertText')
    },
  },
  created() {
    this.validateProps()
    this.loadSubBubbleAgreements()
  },
  methods: {
    ...mapActions({
      getByClientId: 'subBubbleAgreement/getByClientId',
      getByClientGroupId: 'subBubbleAgreement/getByClientGroupId',
      create: 'subBubbleAgreement/create',
      update: 'subBubbleAgreement/update',
      delete: 'subBubbleAgreement/delete',
    }),
    validateProps() {
      if (this.clientId == null && this.clientGroupId == null)
        console.error('Either clientId or clientGroupId must be passed in')
    },
    async loadSubBubbleAgreements() {
      const response = this.isClientGroup
        ? await this.getByClientGroupId(this.clientGroupId)
        : await this.getByClientId(this.clientId)

      if (response.isSuccess) {
        this.subBubbleAgreements = response.data
        this.hasLoaded = true
      } else this.failedToLoad = true
    },
    async saveSubBubbleAgreement(subBubbleAgreement) {
      if (subBubbleAgreement.id)
        await this.updateExistingSubBubbleAgreement(subBubbleAgreement)
      else await this.createNewSubBubbleAgreement(subBubbleAgreement)
    },
    async createNewSubBubbleAgreement(subBubbleAgreement) {
      const createDTO = createSubBubbleAgreementDTOMapper(subBubbleAgreement)
      const response = await this.create(createDTO)
      if (response.isSuccess) {
        this.loadSubBubbleAgreements()
        this.showEditorDialog = false
      }
    },
    async updateExistingSubBubbleAgreement(subBubbleAgreement) {
      const updateDTO = updateSubBubbleAgreementDTOMapper(subBubbleAgreement)
      const response = await this.update(updateDTO)
      if (response.isSuccess) {
        this.loadSubBubbleAgreements()
        this.showEditorDialog = false
      }
    },
    async deleteSubBubbleAgreement() {
      const response = await this.delete(this.subBubbleAgreementDeleting.id)
      if (response.isSuccess) {
        this.loadSubBubbleAgreements()
        this.showConfirmDeleteDialog = false
      }
    },
    onAddNew() {
      this.subBubbleAgreementEditing = new SubBubbleAgreementViewModel({
        clientId: this.clientId,
        clientGroupId: this.clientGroupId,
        startDateLocal: $dayjs().format('YYYY-MM-DDT00:00:00'),
        endDateLocal: $dayjs().add(1, 'month').format('YYYY-MM-DDT23:59:59'),
      })

      this.editorTitle = this.$t(
        'subBubbleAgreements.newSubBubbleAgreementTitle'
      )
      this.showEditorDialog = true
    },
    onEdit(subBubbleAgreement) {
      this.subBubbleAgreementEditing = subBubbleAgreement
      this.editorTitle = this.$t(
        'subBubbleAgreements.editSubBubbleAgreementTitle'
      )
      this.showEditorDialog = true
    },
    onDelete(subBubbleAgreement) {
      this.subBubbleAgreementDeleting = subBubbleAgreement
      this.showConfirmDeleteDialog = true
    },
    onCloseEditor() {
      this.showEditorDialog = false
      this.subBubbleAgreementEditing = null
    },
  },
}
</script>

<template>
  <div>
    <section v-if="failedToLoad">
      <SubBubbleAgreementsFailureBlock
        :is-retrying="isLoading"
        @retry="loadSubBubbleAgreements"
      />
    </section>
    <section v-else>
      <SubBubbleAgreementsListActions v-if="hasLoaded" @on-add-new="onAddNew" />

      <v-alert
        :value="includesNonOwnedAgreements"
        type="info"
        data-testid="non-owned-agreements-alert"
        >{{ nonOwnedAgreementsAlertText }}</v-alert
      >

      <SubBubbleAgreementsList
        :agreements="subBubbleAgreements"
        :is-loading="isLoading"
        :is-client-group="isClientGroup"
        @on-edit="onEdit"
        @on-delete="onDelete"
      />
      <SubBubbleAgreementsEditorDialog
        v-if="showEditorDialog"
        :sub-bubble-agreement="subBubbleAgreementEditing"
        :title="editorTitle"
        :is-saving="isLoadingCRUD"
        @on-save="saveSubBubbleAgreement"
        @close-dialog="onCloseEditor"
      />

      <v-dialog v-model="showConfirmDeleteDialog" max-width="290">
        <v-card>
          <v-card-title class="text-h5">
            {{ $t('subBubbleAgreements.confirmDeleteTitle') }}
          </v-card-title>

          <v-card-text>
            {{ $t('subBubbleAgreements.confirmDeleteMessage') }}
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn text @click="showConfirmDeleteDialog = false">
              {{ $t('general.cancel') }}
            </v-btn>

            <v-btn
              color="green darken-1"
              text
              :loading="isLoadingCRUD"
              @click="deleteSubBubbleAgreement"
            >
              {{ $t('general.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </section>
  </div>
</template>
